/**
 * Stage 1b: Keyword Selection — context-based version using StagePanel.
 *
 * User uploads SEMrush CSV. CSV data is sent directly in the job payload
 * when the user clicks Run — not stored anywhere. The LLM uses it to
 * select primary and secondary keywords, which are written to the document.
 */

import React, { useCallback } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { useCsvUpload } from '../shared/useCsvUpload';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const kw = doc.targeting?.keywords;
  if (!kw?.primary) {
    return <div className="text-sm text-gray-500">No keyword selection yet.</div>;
  }

  const candidates = kw.primary_candidates || [];
  const selectedRationale = candidates.find(
    (c: any) => (typeof c === 'object' ? c?.keyword : c) === kw.primary?.keyword
  );
  const rationale = typeof selectedRationale === 'object' ? (selectedRationale as any)?.rationale : null;

  return (
    <div className="space-y-4">
      {/* Primary keyword card */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Primary Keyword</div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-900">{kw.primary.keyword}</span>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                title="Copy keyword"
                onClick={() => navigator.clipboard.writeText(kw.primary!.keyword)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
              </button>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              {kw.primary.volume != null && <span>Vol: <strong>{kw.primary.volume}</strong></span>}
              {kw.primary.kd != null && <span>KD: <strong>{kw.primary.kd}</strong></span>}
            </div>
            {rationale && (
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">{rationale}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            {kw.keyword_decision?.seo_signal && (
              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${
                kw.keyword_decision.seo_signal === 'strong' ? 'bg-green-100 text-green-700'
                : kw.keyword_decision.seo_signal === 'moderate' ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
              }`}>
                SEO: {kw.keyword_decision.seo_signal}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Secondary keywords */}
      {kw.secondary && kw.secondary.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1.5">Secondary Keywords</div>
          <div className="flex flex-wrap gap-1.5">
            {kw.secondary.map((s, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">
                {s.keyword} <span className="text-gray-400">({s.volume ?? '—'})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Primary candidates */}
      {candidates.length > 1 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1.5">Alternative Primary Keywords</div>
          <div className="space-y-1">
            {candidates.slice(1).map((c: any, i: number) => {
              const ckw = typeof c === 'string' ? c : c?.keyword || '';
              const cvol = typeof c === 'object' ? c?.volume : null;
              const ckd = typeof c === 'object' ? c?.kd : null;
              const crat = typeof c === 'object' ? c?.rationale : null;
              return (
                <div key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
                  <span className="font-medium">{ckw}</span>
                  {cvol != null && <span className="text-gray-400 ml-2">Vol: {cvol}</span>}
                  {ckd != null && <span className="text-gray-400 ml-2">KD: {ckd}</span>}
                  {crat && <p className="text-gray-500 mt-0.5">{crat}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function Stage1bSemrushV2() {
  const { document } = useArticleContext();
  const hasSuggestions = Boolean(document?.targeting?.keywords?.suggestions);

  const { columns, rows, rawCsv, fileName, fileRef, handleFileUpload } = useCsvUpload();

  const buildPayload = useCallback(() => ({ semrush_csv: rawCsv }), [rawCsv]);

  return (
    <StagePanel
      stageId="stage_1b_semrush"
      isLlmStage
      title="Stage 1b: Keyword Selection"
      description="Upload SEMrush CSV, then run to select primary and secondary keywords."
      runDisabled={!hasSuggestions || !rawCsv.trim()}
      runDisabledReason={!hasSuggestions ? 'Requires Stage 1a keyword suggestions' : 'Upload CSV first'}
      renderOutput={renderOutput}
      buildPayload={buildPayload}
    >
      <div className="space-y-3">
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1.5">SEMrush CSV</div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileUpload} />
          <button
            type="button"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => fileRef.current?.click()}
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            {fileName || 'Choose CSV file'}
          </button>
        </div>

        {columns.length > 0 && (
          <div className="border rounded overflow-auto max-h-80">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>{columns.map(col => <th key={col} className="px-2 py-1 text-left font-medium text-gray-600">{col}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.slice(0, 50).map((row, idx) => (
                  <tr key={idx}>{columns.map(col => <td key={`${idx}-${col}`} className="px-2 py-1 text-gray-700">{row[col]}</td>)}</tr>
                ))}
              </tbody>
            </table>
            {rows.length > 50 && <div className="text-xs text-gray-400 px-2 py-1">Showing 50 of {rows.length} rows</div>}
          </div>
        )}

        {columns.length === 0 && (
          <div className="text-xs text-gray-500">No CSV loaded yet. Upload a SEMrush keyword export.</div>
        )}
      </div>
    </StagePanel>
  );
}
